import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Adgangskoden skal være mindst 8 tegn");
    if (password !== confirm) return toast.error("Adgangskoderne er ikke ens");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Din adgangskode er opdateret - log ind med den nye");
      navigate("/login");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center relative px-6">
      <div className="grain" />
      <Link to="/" className="absolute top-6 left-6 z-20 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="auth-back-home">
        <ArrowLeft className="h-4 w-4" /> Tilbage til forsiden
      </Link>
      <div className="relative z-10 w-full max-w-sm">
        <Link to="/" className="flex items-center gap-2 justify-center mb-10">
          <div className="h-7 w-7 rounded-md bg-foreground flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-background" strokeWidth={2} />
          </div>
          <span className="font-display font-bold tracking-tight">Aicom<span className="text-muted-foreground">AI</span></span>
        </Link>

        {!token ? (
          <div className="text-center" data-testid="reset-invalid">
            <h1 className="font-display text-2xl font-medium tracking-tight">Ugyldigt link</h1>
            <p className="text-muted-foreground mt-3 text-sm leading-relaxed">Linket mangler et gyldigt token. Bed om et nyt nulstillingslink.</p>
            <Link to="/glemt-adgangskode" className="mt-8 inline-flex items-center gap-2 text-sm text-foreground hover:underline" data-testid="reset-request-new">
              <ArrowLeft className="h-4 w-4" /> Bed om nyt link
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-display text-3xl font-medium tracking-tight text-center">Ny adgangskode</h1>
            <p className="text-muted-foreground text-center mt-2 text-sm">Vælg en ny adgangskode til din konto.</p>
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Ny adgangskode</Label>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                  data-testid="reset-password-input" placeholder="Mindst 8 tegn" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm">Bekræft adgangskode</Label>
                <Input id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required
                  data-testid="reset-confirm-input" placeholder="••••••••" />
              </div>
              <Button type="submit" className="w-full h-11" disabled={loading} data-testid="reset-submit-btn">
                {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Opdater adgangskode
              </Button>
            </form>
            <p className="text-center text-sm text-muted-foreground mt-6">
              <Link to="/login" className="text-foreground hover:underline" data-testid="reset-login-link">Tilbage til log ind</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
